import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Check, Loader2 } from "lucide-react";
import * as adminApi from "@/api/admin";
import type { EmailSettings, EmailSettingsKind } from "@/api/admin";

const COPY: Record<EmailSettingsKind, { what: string; hint: string }> = {
  messages: {
    what: "contact-form messages",
    hint: "Contact-form submissions are emailed here. Leads have their own settings.",
  },
  leads: {
    what: "new leads",
    hint: "Leads (interest-form submissions) are emailed here. Messages have their own settings.",
  },
};

const DEFAULTS: EmailSettings = {
  email_host: "",
  email_port: 587,
  email_host_user: "",
  email_host_password: "",
  email_use_tls: true,
  default_from_email: "no-reply@paduapp.gr",
  contact_notify_email: "info@paduapp.gr",
};

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-gray-600">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-gray-400">{hint}</p>}
    </div>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
        {description && <p className="text-xs text-gray-400 mt-0.5">{description}</p>}
      </div>
      {children}
    </div>
  );
}

export function EmailSettingsView({ kind }: { kind: EmailSettingsKind }) {
  const [settings, setSettings] = useState<EmailSettings>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    adminApi
      .getEmailSettings(kind)
      .then((data) => setSettings({ ...DEFAULTS, ...data }))
      // If loading fails, start from defaults rather than blocking the page.
      .catch(() => setSettings(DEFAULTS))
      .finally(() => setLoading(false));
  }, [kind]);

  function update<K extends keyof EmailSettings>(key: K, value: EmailSettings[K]) {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(false);
    setSaving(true);
    try {
      const updated = await adminApi.updateEmailSettings(kind, settings);
      setSettings({ ...DEFAULTS, ...updated });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-2xl space-y-4">
        <Skeleton className="h-40 rounded-lg" />
        <Skeleton className="h-72 rounded-lg" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Failed to save the email settings.</AlertDescription>
        </Alert>
      )}

      <Section
        title="Delivery"
        description={`Where ${COPY[kind].what} are sent, and the address they are sent from.`}
      >
        <Field
          label="Destination email"
          hint={COPY[kind].hint}
        >
          <Input
            type="email"
            value={settings.contact_notify_email}
            onChange={(e) => update("contact_notify_email", e.target.value)}
            placeholder="info@paduapp.gr"
          />
        </Field>
        <Field
          label="From address"
          hint="The sender the notification is sent as. Must be an address your SMTP server may send as."
        >
          <Input
            type="email"
            value={settings.default_from_email}
            onChange={(e) => update("default_from_email", e.target.value)}
            placeholder="no-reply@paduapp.gr"
          />
        </Field>
      </Section>

      <Section
        title="SMTP server"
        description="Credentials of the outgoing mail server used to send the emails."
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <Field label="Host">
              <Input
                value={settings.email_host}
                onChange={(e) => update("email_host", e.target.value)}
                placeholder="smtp.yourprovider.com"
              />
            </Field>
          </div>
          <Field label="Port">
            <Input
              type="number"
              value={String(settings.email_port)}
              onChange={(e) => update("email_port", Number(e.target.value))}
              placeholder="587"
            />
          </Field>
        </div>

        <Field label="Username">
          <Input
            value={settings.email_host_user}
            onChange={(e) => update("email_host_user", e.target.value)}
            placeholder="SMTP username"
            autoComplete="off"
          />
        </Field>

        <Field label="Password" hint="Stored on the server. Leave unchanged to keep the current one.">
          <Input
            type="password"
            value={settings.email_host_password}
            onChange={(e) => update("email_host_password", e.target.value)}
            placeholder="••••••••"
            autoComplete="new-password"
          />
        </Field>

        <label className="flex items-center gap-2 pt-1 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={settings.email_use_tls}
            onChange={(e) => update("email_use_tls", e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 accent-[#00d9d9] focus:ring-[#00d9d9]/40"
          />
          <span className="text-sm text-gray-700">Use TLS</span>
        </label>
      </Section>

      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={saving}
          className="bg-[#00d9d9] hover:bg-[#00bfc0] text-white"
        >
          {saving ? (
            <Loader2 size={14} className="animate-spin" />
          ) : saved ? (
            <>
              <Check size={14} className="mr-1" /> Saved
            </>
          ) : (
            "Save settings"
          )}
        </Button>
      </div>
    </form>
  );
}
